// 1Hz countdown against a RetroTimer snapshot. Returns the current
// `remainingMs` and an `expired` flag once the countdown crosses 0.
//
// The hook ticks via setInterval(1000); for sub-second updates (e.g. animating
// a progress ring) you'd swap to requestAnimationFrame, but the spec says
// 1Hz is fine for the mm:ss readout.
//
// We don't subscribe to the timer field directly — the parent already does
// that via useRetroMeta and passes the resolved RetroTimer in. That keeps
// this hook a pure function of (timer, wall clock).

import { useEffect, useState } from 'react';
import type { RetroTimer } from '@frame/shared';

export interface TimerSnapshot {
  remainingMs: number;
  expired: boolean;
  paused: boolean;
}

export function useTimer(timer: RetroTimer | undefined): TimerSnapshot {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!timer || timer.paused) return;
    // Tick once a second on the wall-clock second boundary so two clients
    // showing the same countdown advance roughly in sync.
    const tick = () => setNow(Date.now());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [timer]);

  if (!timer) return { remainingMs: 0, expired: false, paused: false };

  // When paused, the displayed remaining time freezes at `pausedAt → endsAt`.
  // When running, it's `now → endsAt`. Negative remainings mean expired.
  const reference = timer.paused ? timer.pausedAt ?? now : now;
  const remainingMs = Math.max(0, timer.endsAt - reference);
  return {
    remainingMs,
    expired: timer.endsAt - reference <= 0,
    paused: timer.paused,
  };
}

// Format ms as "M:SS" (no leading zero on minutes). Used by TimerWidget +
// the style guide.
export function formatRemaining(ms: number): string {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
